import { CreatedServiceAccountIdentityResponseDto, CreatedServiceAccountResponseDto, ListServiceAccountGroupsResponseDto, ListServiceAccountResponseDto, ListServiceIdentitiesResponseDto, ServiceAccountIdentityCreateDto, ServiceAccountResponseDto, WorkspaceServiceAccountCreateDto, } from '@lucidlink/dto/web-service-api';
import { cryptoKeyToBase64, decryptAesKeyWithPrivateKey, encryptCryptoKeyWithPublicKey, generateAesGcmKey, generateAesKwKey, } from '@lucidlink/ts-crypto/crypto';
import { InternalError } from '@lucidlink/web-fs/common';
import { parserFromDto } from '#fetcher';
import { prepareServiceAccountInvitation, wrapAesKeyWithAesKey, } from '#sdk/common/index';
export const createServiceAccountManager = ({ workspaceId, workspaceFetcher, getAdminCrypto, getMemberCrypto, getPrincipalsCrypto, loginToWorkspace, serviceAccountsMap, }) => {
    return new ServiceAccountManagerImpl(workspaceId, workspaceFetcher, getAdminCrypto, getMemberCrypto, getPrincipalsCrypto, loginToWorkspace, serviceAccountsMap);
};
const createServiceAccountTokenString = async (args) => `${args.prefix}:${args.webServiceToken}:${await cryptoKeyToBase64(args.serviceIdentitySecretKey)}`;
const prepareInitialServiceIdentity = async (accountKey, expirationOptions) => {
    const secretKey = await generateAesKwKey();
    const eAccountKeyByServiceIdentitySecretKey = await wrapAesKeyWithAesKey(accountKey, secretKey);
    return {
        secretKey,
        createDto: {
            eAccountKeyByServiceIdentitySecretKey,
            ...expirationOptions,
            isSingleUse: expirationOptions.isSingleUse ?? false,
        },
    };
};
class ServiceAccountManagerImpl {
    workspaceId;
    workspaceFetcher;
    getAdminCrypto;
    getMemberCrypto;
    getPrincipalsCrypto;
    loginToWorkspace;
    serviceAccountsMap;
    serviceAccountFetcher;
    constructor(workspaceId, workspaceFetcher, getAdminCrypto, getMemberCrypto, getPrincipalsCrypto, loginToWorkspace, serviceAccountsMap) {
        this.workspaceId = workspaceId;
        this.workspaceFetcher = workspaceFetcher;
        this.getAdminCrypto = getAdminCrypto;
        this.getMemberCrypto = getMemberCrypto;
        this.getPrincipalsCrypto = getPrincipalsCrypto;
        this.loginToWorkspace = loginToWorkspace;
        this.serviceAccountsMap = serviceAccountsMap;
        this.serviceAccountFetcher = this.workspaceFetcher.createSubFetcher({
            path: ['service-accounts'],
        });
    }
    async createServiceAccount(name, expirationOptions = {}, role) {
        const accountKey = await generateAesGcmKey();
        const initialIdentity = await prepareInitialServiceIdentity(accountKey, expirationOptions);
        const { serviceIdentity, ...serviceAccount } = await this.postServiceAccount({
            name,
            accountKey,
            role,
            serviceIdentity: initialIdentity.createDto,
        });
        if (serviceIdentity === undefined) {
            throw new InternalError('An initial service identity was requested but not returned');
        }
        const { prefix, webServiceToken, ...serviceIdentityRest } = serviceIdentity;
        return {
            ...serviceAccount,
            serviceIdentity: {
                ...serviceIdentityRest,
                serviceAccountToken: await createServiceAccountTokenString({
                    prefix,
                    webServiceToken,
                    serviceIdentitySecretKey: initialIdentity.secretKey,
                }),
            },
        };
    }
    async createServiceAccountWithoutIdentity(name, role) {
        const accountKey = await generateAesGcmKey();
        const { serviceIdentity, ...serviceAccount } = await this.postServiceAccount({ name, accountKey, role });
        return serviceAccount;
    }
    async postServiceAccount(params) {
        const { name, accountKey, role, serviceIdentity } = params;
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const memberCrypto = await this.getMemberCrypto();
        const { invitation } = await prepareServiceAccountInvitation({
            adminCrypto,
            workspaceId: this.workspaceId,
            accountKey,
            creatorRole: memberCrypto.member.role,
            ...(role !== undefined && { targetRole: role }),
        });
        const adminKeys = await adminCrypto.verifyAndGetAdminKeys();
        const eAccountKeyByAdminKey = await encryptCryptoKeyWithPublicKey(accountKey, adminKeys.encryptPair.publicKey);
        const serviceAccountRequest = {
            serviceAccount: {
                name,
                eAccountKeyByAdminKey,
            },
            invitation,
            ...(serviceIdentity !== undefined && { serviceIdentity }),
            ...(role !== undefined && { role }),
        };
        const serviceAccount = await this.serviceAccountFetcher.post([], {
            parser: parserFromDto(CreatedServiceAccountResponseDto),
            body: WorkspaceServiceAccountCreateDto.create(serviceAccountRequest),
        });
        (await this.getPrincipalsCrypto()).refresh();
        this.serviceAccountsMap.reset();
        return serviceAccount;
    }
    async listServiceAccounts(options = {}) {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        return this.serviceAccountFetcher.get([], {
            parser: parserFromDto(ListServiceAccountResponseDto),
            queryParams: options,
        });
    }
    async getServiceAccount(serviceAccountId, options = {}) {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        return this.serviceAccountFetcher.get([serviceAccountId], {
            parser: parserFromDto(ServiceAccountResponseDto),
            queryParams: options,
        });
    }
    async deleteServiceAccount(serviceAccountId) {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        await this.serviceAccountFetcher.delete([serviceAccountId]);
        (await this.getPrincipalsCrypto()).refresh();
        this.serviceAccountsMap.reset();
    }
    async createServiceAccountIdentity(serviceAccountId, expirationOptions) {
        await this.loginToWorkspace();
        const adminCrypto = await this.getAdminCrypto();
        const adminKeys = await adminCrypto.verifyAndGetAdminKeys();
        const serviceAccount = await this.getServiceAccount(serviceAccountId);
        const [accountKey, serviceIdentitySecretKey] = await Promise.all([
            decryptAesKeyWithPrivateKey(serviceAccount.eAccountKeyByAdminKey, adminKeys.encryptPair.privateKey),
            generateAesKwKey(),
        ]);
        const eAccountKeyByServiceIdentitySecretKey = await wrapAesKeyWithAesKey(accountKey, serviceIdentitySecretKey);
        const { prefix, webServiceToken, ...serviceIdentityRest } = await this.serviceAccountFetcher.post([serviceAccountId, 'identities'], {
            parser: parserFromDto(CreatedServiceAccountIdentityResponseDto),
            body: ServiceAccountIdentityCreateDto.create({
                eAccountKeyByServiceIdentitySecretKey,
                ...expirationOptions,
            }),
        });
        return {
            ...serviceIdentityRest,
            serviceAccountToken: await createServiceAccountTokenString({
                prefix,
                webServiceToken,
                serviceIdentitySecretKey,
            }),
        };
    }
    async listServiceAccountIdentities(serviceAccountId, options = {}) {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        return this.serviceAccountFetcher.get([serviceAccountId, 'identities'], {
            parser: parserFromDto(ListServiceIdentitiesResponseDto),
            queryParams: options,
        });
    }
    async deleteServiceAccountIdentity(serviceAccountId, identityId) {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        await this.serviceAccountFetcher.delete([
            serviceAccountId,
            'identities',
            identityId,
        ]);
    }
    async listServiceAccountGroups(serviceAccountId) {
        await this.loginToWorkspace();
        await this.getAdminCrypto();
        return this.serviceAccountFetcher.get([serviceAccountId, 'groups'], {
            parser: parserFromDto(ListServiceAccountGroupsResponseDto),
        });
    }
}
